import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";

interface PaginationFooterProps {
  /** The page currently reflected by the data on screen (1-based). */
  page: number;
  pageSize: number;
  total: number;
  /** Disables the controls while a fetch is in flight. */
  loading?: boolean;
  onPageChange: (page: number) => void;
}

/**
 * Server-side pagination footer shared by the admin explorers: a "from–to of
 * total" summary plus prev/next controls derived from the page/total.
 */
export function PaginationFooter({
  page,
  pageSize,
  total,
  loading,
  onPageChange,
}: PaginationFooterProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted">
        {from}–{to} of {total}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1 || loading}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          <ChevronLeft className="h-4 w-4" />
          Prev
        </Button>
        <span className="text-muted">
          Page {page} of {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= totalPages || loading}
          onClick={() => onPageChange(page + 1)}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
